class DynamicPagination extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.pageSyncFrame = null;
    this.baseTitle = '';
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick);
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    this.baseTitle = document.title.replace(/\s+\|\s+Página\s+\d+$/, '');
    this.schedulePageSync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    window.removeEventListener('scroll', this.handleScroll);

    if (this.pageSyncFrame) {
      window.cancelAnimationFrame(this.pageSyncFrame);
      this.pageSyncFrame = null;
    }
  }

  handleScroll() {
    this.schedulePageSync();
  }

  schedulePageSync() {
    if (this.pageSyncFrame) return;

    this.pageSyncFrame = window.requestAnimationFrame(() => {
      this.pageSyncFrame = null;
      this.syncVisiblePage();
    });
  }

  syncVisiblePage() {
    const productGrid = document.getElementById('product-grid');
    const pageItems = productGrid?.querySelectorAll('[data-dynamic-page]');
    if (!pageItems?.length) return;

    const pageAnchors = new Map();
    pageItems.forEach((item) => {
      const page = Number(item.dataset.dynamicPage);
      if (page && !pageAnchors.has(page)) pageAnchors.set(page, item);
    });

    const firstPage = pageAnchors.keys().next().value;
    if (!firstPage) return;

    const viewportAnchor = window.innerHeight * 0.35;
    let activePage = firstPage;

    pageAnchors.forEach((anchor, page) => {
      if (anchor.getBoundingClientRect().top <= viewportAnchor) activePage = page;
    });

    this.syncUrl(activePage);
  }

  syncUrl(page) {
    const pageUrl = new URL(window.location.href);
    pageUrl.searchParams.delete('section_id');

    if (page > 1) {
      pageUrl.searchParams.set('page', page);
    } else {
      pageUrl.searchParams.delete('page');
    }

    pageUrl.hash = '';
    const absoluteUrl = pageUrl.toString();

    if (absoluteUrl !== window.location.href) {
      window.history.replaceState({ path: absoluteUrl }, '', absoluteUrl);
    }

    if (this.baseTitle) {
      document.title = page > 1 ? `${this.baseTitle} | Página ${page}` : this.baseTitle;
    }

    document.querySelectorAll('link[rel="canonical"]').forEach((canonical) => {
      canonical.setAttribute('href', absoluteUrl);
      canonical.href = absoluteUrl;
    });

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', absoluteUrl);
      ogUrl.content = absoluteUrl;
    }
  }

  async handleClick(event) {
    const button = event.target.closest('[data-dynamic-pagination-button]');
    if (!button || !this.contains(button) || button.disabled) return;

    const nextPage = Number(this.dataset.nextPage);
    const sectionId = this.dataset.sectionId;
    if (!nextPage || !sectionId) return;

    const requestUrl = new URL(window.location.href);
    requestUrl.searchParams.delete('page');
    requestUrl.searchParams.set('page', nextPage);
    requestUrl.searchParams.set('section_id', sectionId);

    this.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');
    button.disabled = true;

    try {
      const response = await fetch(requestUrl.toString(), {
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) throw new Error(`Dynamic pagination request failed: ${response.status}`);

      const responseHtml = await response.text();
      const responseDocument = new DOMParser().parseFromString(responseHtml, 'text/html');
      const sourceGrid = responseDocument.getElementById('product-grid');
      const currentGrid = document.getElementById('product-grid');
      const sourcePagination = responseDocument.querySelector('dynamic-pagination');

      if (!sourceGrid || !currentGrid) throw new Error('Dynamic pagination grid was not found.');

      Array.from(sourceGrid.children).forEach((productItem) => {
        currentGrid.appendChild(productItem);
      });

      if (sourcePagination) {
        this.dataset.currentCount = sourcePagination.dataset.currentCount || '';
        this.dataset.totalCount = sourcePagination.dataset.totalCount || '';

        if (sourcePagination.dataset.nextPage) {
          this.dataset.nextPage = sourcePagination.dataset.nextPage;
        } else {
          delete this.dataset.nextPage;
        }

        this.innerHTML = sourcePagination.innerHTML;
      }

      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger(currentGrid.innerHTML);
      }

      this.schedulePageSync();
    } catch (error) {
      console.error(error);
      button.disabled = false;
    } finally {
      this.classList.remove('is-loading');
      this.setAttribute('aria-busy', 'false');

      const nextButton = this.querySelector('[data-dynamic-pagination-button]');
      if (nextButton && this.dataset.nextPage) nextButton.disabled = false;
    }
  }
}

customElements.define('dynamic-pagination', DynamicPagination);
